import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useFetch } from "../../../utils/hooks/useFetch";
import { getProjectTaskServiceConfig } from "../../../utils/services/projects";
import { getHolidaysListForCurrentWeekServiceConfig } from "../../../utils/services/Holidays";
import { getComparator } from "../../../utils/helpers";
import { translate } from "../../../utils/translations/translate";
import {
  unWindData,
  generateWeekDaysInfo,
  formatTimeEntriesForReports,
} from "../../../utils/helpers";
import {
  saveOrSubmitTimeSheetRequestConfig,
  updateTimeSheetRequestConfig,
  getFormattedTimeEntriesData,
  deleteTimesheetAttachmentConfig,
} from "../../../utils/services/timesheet";
import { useUpdateCount } from "../../Header/updateCount";
import { useTimesheetDetails } from "./useTimesheetDetails";
import {
  exportTimesheetToPDF,
  generateTimesheetExcel,
} from "../../../utils/reports";
import { TIMESHEET_ACTION_STATES } from "../../../utils/constants";

export const useTimesheetCorrection = (timesheetId) => {
  const {
    fetchTimesheetDetails,
    isLoading: isPageBusy,
    selectedWeek,
    toastInfo,
    setToastInfo,
  } = useTimesheetDetails();
  const { isError, isLoading, performAction, hideServiceError } = useFetch();
  const [showDraftAction, setShowDraftAction] = useState(false);
  const [showSubmitButton, setShowSubmitButton] = useState(true);
  const [projectsList, setProjectsList] = useState([]);
  const [holidaysList, setHolidaysList] = useState([]);
  const [currentWeekHolidays, setCurrentWeekHolidays] = useState([]);
  const [projectTasksList, setProjectTasksList] = useState([]);
  const timesheetDetails = useRef({});
  const updateCount = useUpdateCount();
  const weekDaysArr = useMemo(() => {
    return generateWeekDaysInfo(
      selectedWeek.firstDay,
      selectedWeek.lastDay,
      currentWeekHolidays
    );
  }, [selectedWeek, currentWeekHolidays]);
  const fetchMetaInfo = useCallback(
    async (timesheetId) => {
      try {
        const { url: projectTasksURL, options: projectTasksOptions } =
          getProjectTaskServiceConfig();
        const timesheetMetaInfo = await fetchTimesheetDetails(timesheetId);
        if (typeof timesheetMetaInfo === "object") {
          const { timesheetDetailsInfo, timsheetRange } = timesheetMetaInfo;
          if (timesheetDetailsInfo.projectId) {
            setProjectsList([timesheetDetailsInfo]);
            const status = timesheetDetailsInfo.status;
            if (
              !status ||
              status?.toLowerCase() ===
                TIMESHEET_ACTION_STATES.DRAFT.toLowerCase()
            ) {
              setShowDraftAction(true);
            }
            if (
              status?.toLowerCase() ===
              TIMESHEET_ACTION_STATES.APPROVED.toLowerCase()
            ) {
              setShowSubmitButton(false);
            }
            const { firstDay, lastDay } = timsheetRange;
            const { url: holidaysListURL, options: holidaysOptions } =
              getHolidaysListForCurrentWeekServiceConfig({
                startDate: firstDay,
                endDate: lastDay,
              });
            const dependentResponses = await Promise.allSettled([
              performAction(projectTasksURL, projectTasksOptions),
              performAction(holidaysListURL, holidaysOptions),
            ]);
            const [projectTasksPromise, holidayListPromise] =
              dependentResponses;
            if (
              projectTasksPromise.status === "fulfilled" &&
              Array.isArray(projectTasksPromise.value?.Result)
            ) {
              const filteredProjects = projectTasksPromise.value.Result.filter(
                (task) => task.IsActive
              );
              const formattedProjectTasks = filteredProjects.map((task) => {
                const { ProjectTaskID, ProjectTaskName } = task;
                return {
                  label: ProjectTaskName,
                  value: ProjectTaskID,
                  option: {
                    taskId: ProjectTaskID,
                    taskName: ProjectTaskName,
                  },
                };
              });
              setProjectTasksList(formattedProjectTasks);
            }
            if (
              holidayListPromise.status === "fulfilled" &&
              Array.isArray(holidayListPromise.value?.Result)
            ) {
              const formattedHolidays = holidayListPromise.value?.Result.map(
                (holiday) => {
                  const { HolidayDate, HolidayName } = holiday;
                  return {
                    holidayDate: HolidayDate,
                    holidayName: HolidayName,
                  };
                }
              );
              const holidayListForWeek = formattedHolidays.map((holiday) =>
                getComparator(holiday.holidayDate)
              );
              setHolidaysList(formattedHolidays);
              setCurrentWeekHolidays(holidayListForWeek);
            }
          } else {
            setToastInfo({
              message: translate("labels.noAssociatedTimeEntries"),
              type: "warning",
            });
            return;
          }
          return;
        }
        throw new Error("Unable to fetch details");
      } catch (e) {
        console.error("Error fetching the meta information", e);
        setToastInfo({
          message: e.message,
          type: "error",
          autoExpire: true,
        });
      }
    },
    [performAction, setToastInfo, fetchTimesheetDetails]
  );
  const syncTimesheetDetails = useCallback((projectId, data) => {
    const prevInfo = timesheetDetails.current[projectId];
    let lastSubmitted = null;
    if (prevInfo) {
      lastSubmitted = prevInfo.lastSubmittedOn;
    }
    timesheetDetails.current[projectId] = {
      ...data,
      lastSubmittedOn: lastSubmitted || data.lastSubmittedOn,
    };
  }, []);
  const handleToastClose = useCallback(() => {
    setToastInfo(null);
  }, [setToastInfo]);
  const handleTimesheetModeChange = useCallback((projectId, mode) => {
    setProjectsList((prev) => {
      const updatedProjects = [...prev];
      const projectIdx = updatedProjects.findIndex(
        (project) => project.projectId === projectId
      );
      if (projectIdx > -1) {
        updatedProjects[projectIdx].mode = mode;
        if (mode === "edit") {
          updatedProjects[projectIdx].showEditSwitch = true;
        } else {
          updatedProjects[projectIdx].showEditSwitch = false;
        }
      }
      return updatedProjects;
    });
  }, []);
  const onDeleteAttachment = useCallback(async (timesheetId, fileName) => {
    try {
      const { url, options } = deleteTimesheetAttachmentConfig(
        timesheetId,
        fileName
      );
      const response = await performAction(url, options);
      if (response.StatusCode === 200) {
        setToastInfo({
          message: `${response.Message}`,
          autoExpireToast: true,
          type: "success",
        });
        return true;
      }
    } catch (e) {
      console.error("Error submitting timesheets");
    } finally {
      window.scrollTo({ top: 0, behavior: "smooth" });
      updateCount();
    }
  });
  const handleTimesheetSubmitActions = useCallback(
    async (action) => {
      try {
        var entriesArr = projectsList
          .filter((project) => project.mode === "edit")
          .map((project) => {
            const refData = timesheetDetails.current[project.projectId] || {};
            const timeEntryDetails = unWindData(refData.tasks || []);
            return {
              status:
                action === TIMESHEET_ACTION_STATES.DRAFT
                  ? TIMESHEET_ACTION_STATES.DRAFT
                  : TIMESHEET_ACTION_STATES.SUBMITTED,
              projectId: project.projectId,
              projectName: project.projectName,
              timesheetId: project.timesheetId,
              comments: refData.comments,
              timeEntryDetails,
              attachments: refData.attachments,
            };
          });
        if (entriesArr.length === 0) {
          setToastInfo({
            message: translate("labels.notChangesToSave"),
            type: "info",
            autoExpire: true,
          });
          return;
        }

        entriesArr = entriesArr.map((entry) => {
          const cleanedAttachments = entry.attachments
            .filter(
              (a) => a.file !== null && typeof a.file.orignalFile !== "string"
            )
            .map((a) => a.file.orignalFile);
          const res = cleanedAttachments?.length
            ? cleanedAttachments?.filter((item) => item !== undefined)
            : [];
          return {
            ...entry,
            attachments: res,
          };
        });

        const vacationDays = weekDaysArr.filter(
          (week) => week.isHoliday || week.isFutureDate
        ).length;
        const totalWeekDays = weekDaysArr.length;
        const requiredDays = totalWeekDays - vacationDays;
        const incompleteInfoProjects = entriesArr
          .filter((entry) =>
            entry.timeEntryDetails.some((tmpEntry) => tmpEntry.isInCompleteRow)
          )
          .map((entry) => entry.projectId);
        const missingTimeEntriesProjects = entriesArr
          .filter(
            (entry) =>
              entry.timeEntryDetails.filter((tEn) => tEn.hours !== null)
                .length < requiredDays
          )
          .map((entry) => entry.projectId);
        const hasAtleastOneTimeEntry = entriesArr.some((entry) =>
          entry.timeEntryDetails.some((tEntry) => tEntry.hours !== null)
        );
        const noDetailsProjects = entriesArr
          .filter((entry) =>
            entry.timeEntryDetails.every(
              (tEn) => !tEn.taskId && tEn.hours === 0
            )
          )
          .map((entry) => entry.projectId);
        const filteredNoInfoProjects = incompleteInfoProjects.filter(
          (project) => !noDetailsProjects.includes(project)
        );
        if (filteredNoInfoProjects.length > 0) {
          setToastInfo({
            message: `Unable to proceed with ${action} action Projects:${incompleteInfoProjects.join(
              ","
            )} has missing Project task info`,
            type: "info",
            autoExpire: true,
          });
          return;
        }
        if (action !== "Draft" && missingTimeEntriesProjects.length > 0) {
          setToastInfo({
            message: `Unable to proceed with submit action Projects:${missingTimeEntriesProjects.join(
              ","
            )} has missing time entries`,
            type: "error",
            autoExpire: true,
          });
          return;
        }
        const projectsToBeSubmitted = entriesArr
          .filter(
            (entry) =>
              entry.timeEntryDetails.filter((tEntry) => tEntry.taskId).length >
              0
          )
          .filter((entry) => !noDetailsProjects.includes(entry.projectId));
        if (projectsToBeSubmitted.length === 0 || !hasAtleastOneTimeEntry) {
          setToastInfo({
            message: `Please enter time details to proceed with ${action}`,
            type: "error",
            autoExpire: true,
          });
          return;
        }

        const validateAttachments = (data) => {
          const invalidProjects = [];

          data.forEach((item) => {
            if (item.status === "Draft") return;

            const totalHours = item.timeEntryDetails.reduce(
              (sum, detail) => sum + (Number(detail.hours) || 0),
              0
            );

            const hasAttachment = item.attachments.length;
            // && item.attachments.some((att) => att?.file != null);

            if (totalHours > 0 && hasAttachment === 0) {
              invalidProjects.push(item.projectName || item.projectId);
            }
          });

          return invalidProjects;
        };

        // Usage
        const invalidProjects = validateAttachments(entriesArr);
        if (invalidProjects.length > 0) {
          setToastInfo({
            message: `Please upload attachments for project(s): ${invalidProjects.join(
              ", "
            )}`,
            type: "error",
            autoExpireToast: true,
          });
          return;
        }

        const reqDetails = projectsToBeSubmitted.map((project) => {
          const timsheetReqDetails = project.timesheetId
            ? updateTimeSheetRequestConfig(project)
            : saveOrSubmitTimeSheetRequestConfig(project);
          return timsheetReqDetails;
        });
        const responsePromises = await Promise.allSettled(
          reqDetails.map((req) => {
            return performAction(req.url, req.options);
          })
        );
        const successFulIds = [];
        const failedIds = [];
        responsePromises.forEach((response, idx) => {
          if (
            response.status === "fulfilled" &&
            response.value.StatusCode === 200
          ) {
            successFulIds.push(projectsToBeSubmitted[idx]?.projectName);
            if (projectsToBeSubmitted[idx]?.projectId && action !== "Draft") {
              const savedInfo =
                timesheetDetails.current[projectsToBeSubmitted[idx].projectId];
              timesheetDetails.current[projectsToBeSubmitted[idx]?.projectId] =
                {
                  ...savedInfo,
                  lastSubmittedOn: new Date(),
                };
            }
          } else {
            failedIds.push(projectsToBeSubmitted[idx]?.projectName);
          }
        });
        if (successFulIds.length > 0 && failedIds.length === 0) {
          const msg =
            action === TIMESHEET_ACTION_STATES.DRAFT
              ? translate("labels.timesheetDraftToast")
              : translate("labels.timesheetSubmitToast");
          setToastInfo({
            message: `${msg} ${successFulIds}`,
            autoExpire: true,
            type: "success",
          });
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        } else {
          setToastInfo({
            message: `Action: ${action} could not be completed for the following project: ${failedIds.join(
              ", "
            )}.`,
            autoExpire: true,
            type: "error",
          });
        }
      } catch (e) {
        console.error("Error submitting timesheets");
      } finally {
        window.scrollTo({ top: 0, behavior: "smooth" });
        updateCount();
      }
    },
    [projectsList, weekDaysArr, performAction, setToastInfo]
  );
  const getRefProjectData = (projectId) => {
    const refProject = timesheetDetails.current[projectId];
    if (Array.isArray(refProject?.tasks)) {
      const unwindedData = unWindData(refProject.tasks || []);
      const transformedTimeEntries = unwindedData.map((tEntry) => {
        return {
          ProjectTaskID: tEntry.taskId,
          TaskType: tEntry.taskType,
          PayCode: tEntry.payCode,
          Hours: tEntry.payloadHours,
          Date: tEntry.date,
          ProjectTaskName: tEntry.taskName,
        };
      });
      const payloadEntries = getFormattedTimeEntriesData(
        transformedTimeEntries
      );

      return formatTimeEntriesForReports(payloadEntries);
    }
    return [];
  };

  const getReportsData = () => {
    const dataToBeDownloaded = projectsList.map((project) => {
      return {
        ...project,
        lastSubmittedOn:
          timesheetDetails.current[project.projectId]?.lastSubmittedOn ||
          project.lastSubmittedOn,
        timesheetData:
          project.mode === "read"
            ? project.timesheetData
            : getRefProjectData(project.projectId),
      };
    });
    return dataToBeDownloaded;
  };
  const generateReport = (format) => {
    const reportData = getReportsData();
    if (format === "excel") {
      generateTimesheetExcel(reportData);
    } else {
      exportTimesheetToPDF(reportData);
    }
  };
  useEffect(() => {
    fetchMetaInfo(timesheetId);
  }, [fetchMetaInfo, timesheetId]);
  useEffect(() => {
    if (toastInfo) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [toastInfo]);
  return {
    error: isError,
    isLoading: isPageBusy || isLoading,
    projectsList,
    holidaysList,
    projectTasksList,
    syncTimesheetDetails,
    handleTimesheetSubmitActions,
    toastInfo,
    currentWeekHolidays,
    weekDaysArr,
    selectedWeek,
    showDraftAction,
    showSubmitButton,
    handleTimesheetModeChange,
    generateReport,
    handleToastClose,
    hideServiceError,
    timesheetInfo: timesheetDetails.current,
    onDeleteAttachment,
  };
};
